import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { navigate } from "../router.tsx";

/**
 * Row anchors and focus for grouped settings pages (every settings screen): `#setting-<key>`
 * names the row a link or a search pick opens on.
 */
const SETTING_HASH = "#setting-";
export const settingId = (key: string) => `setting-${key}`;
export const settingHash = (key: string) => `${SETTING_HASH}${encodeURIComponent(key)}`;

/**
 * A setting row as an anchor target: focusable from a search pick or a summary link, which
 * mark it for a moment (`data-found`: a tint over the focus outline).
 */
export const SETTING_ROW_CLASS =
  "@container flex scroll-mt-6 flex-col gap-3 px-4 py-3.5 transition-colors duration-(--duration-base) outline-none first:rounded-t-lg last:rounded-b-lg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus focus-visible:outline-solid data-found:bg-surface-selected data-found:outline-2 data-found:outline-solid data-found:-outline-offset-2 data-found:outline-focus";

/** The setting an anchor names, if any (a malformed one names none). */
export function hashTarget(): string | null {
  if (!window.location.hash.startsWith(SETTING_HASH)) {
    return null;
  }
  try {
    return decodeURIComponent(window.location.hash.slice(SETTING_HASH.length));
  } catch {
    return null;
  }
}

/** Scrolls a setting's row into view, moves focus to it and marks it for a moment. */
function reveal(key: string) {
  const row = document.getElementById(settingId(key));
  if (!row) {
    return;
  }
  markFound(row, "center");
}

/**
 * Scrolls an anchor row into view, moves focus to it and marks it for a moment (`data-found`,
 * ~2.4 s): settings rows, and the source rows "Show in sources" reaches.
 */
export function markFound(row: HTMLElement, block: ScrollLogicalPosition) {
  row.scrollIntoView?.({ block });
  row.focus({ preventScroll: true });
  row.dataset.found = "";
  window.setTimeout(() => {
    delete row.dataset.found;
  }, 2400);
}

/**
 * Focus follows navigation between a grouped page's groups: onto the setting picked or named
 * by the URL's anchor (a deep link, Back or Forward), else onto the new group's heading (not
 * on the first page shown). An anchor to a setting of another group opens that group. Returns
 * `pick`, which opens a setting's group and focuses its row (a search pick, a summary link).
 */
export function useGroupFocus<G extends string>(props: {
  group: G;
  /** The settings shown, once loaded: an anchor naming none of them is ignored. */
  settings: readonly { key: string }[] | undefined;
  groupOf: (key: string) => G;
  /** A setting's group page with its anchor. */
  pathOf: (key: string) => string;
  heading: RefObject<HTMLElement | null>;
}): (key: string) => void {
  const { group, settings, heading } = props;
  // A pick to reveal once its page shows.
  const [target, setTarget] = useState<{ key: string } | null>(null);
  const shown = useRef<G | null>(null);
  // The latest mapping, without re-running the effect when a caller passes new closures.
  const paths = useRef(props);
  useLayoutEffect(() => {
    paths.current = props;
  });
  // A group change moves focus to its heading at once, even while the settings still load (a
  // setting to reveal takes it over once they do).
  const previous = useRef<G | null>(null);
  useEffect(() => {
    if (previous.current !== null && previous.current !== group) {
      heading.current?.focus();
    }
    previous.current = group;
  }, [group, heading]);
  useEffect(() => {
    if (!settings) {
      return;
    }
    const { groupOf, pathOf } = paths.current;
    const first = shown.current === null;
    const moved = shown.current !== group;
    shown.current = group;
    if (target) {
      setTarget(null);
    }
    const named = target?.key ?? (moved ? hashTarget() : null);
    const key = named && settings.some((s) => s.key === named) ? named : null;
    if (key && groupOf(key) !== group) {
      // An anchor to a setting of another page (an old link): open that page.
      navigate(pathOf(key), { replace: true });
      setTarget({ key });
    } else if (key) {
      reveal(key);
    } else if (moved && !first) {
      heading.current?.focus();
    }
  }, [group, target, settings, heading]);
  return (key: string) => {
    const { groupOf, pathOf } = paths.current;
    navigate(pathOf(key), { replace: groupOf(key) === group });
    setTarget({ key });
  };
}
